import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type AdminApiKeyCreatedResponse,
  type AdminCreateApiKeyRequest,
  type AdminUserDetailResponse,
  type AdminUserListResponse,
  type AdminUserPatchRequest,
  type AdminUserSummary,
  type AdminUserUnlockResponse,
  adminApiClient,
} from "@/lib/api/client";
import { getApiErrorMessage } from "@/lib/api/errors";

import type { UserFilters } from "./filters";
import { adminKeys } from "./keys";
import {
  globalUsageQueryOptions,
  overviewQueryOptions,
  userDetailQueryOptions,
  userUsageQueryOptions,
  usersQueryOptions,
} from "./queries";

type UpdateUserVariables = {
  userId: string;
  patch: AdminUserPatchRequest;
};

type IssueKeyVariables = {
  name: string;
};

type RevokeKeyVariables = {
  keyId: string;
};

/** Paginated admin user list for the given filters. */
export function useUsers(filters: UserFilters) {
  return useQuery(usersQueryOptions(filters));
}

/** Admin user detail with keys and recent audit entries. */
export function useUserDetail(userId: string) {
  return useQuery(userDetailQueryOptions(userId));
}

/** Per-user usage time series for the given UTC range. */
export function useUserUsage(userId: string, fromISO: string, toISO: string) {
  return useQuery(userUsageQueryOptions(userId, fromISO, toISO));
}

/** Aggregate admin dashboard metrics (users, requests, top endpoints). */
export function useStatsOverview() {
  return useQuery(overviewQueryOptions());
}

/** Platform-wide usage time series for the given UTC range. */
export function useGlobalUsage(fromISO: string, toISO: string) {
  return useQuery(globalUsageQueryOptions(fromISO, toISO));
}

/** Update a user (tier, role, is_active) with optimistic cache updates. */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, patch }: UpdateUserVariables): Promise<AdminUserSummary> => {
      const { data, error, response } = await adminApiClient.PATCH(
        "/api/v1/admin/users/{user_id}",
        {
          params: { path: { user_id: userId } },
          body: patch,
        },
      );

      if (!response.ok || error || !data) {
        throw new Error(getApiErrorMessage(error, "Failed to update user."));
      }

      return data;
    },
    onMutate: async ({ userId, patch }) => {
      await queryClient.cancelQueries({ queryKey: adminKeys.usersRoot });
      await queryClient.cancelQueries({ queryKey: adminKeys.userDetailRoot });

      const previousListQueries = queryClient.getQueriesData<AdminUserListResponse>({
        queryKey: adminKeys.usersRoot,
      });
      const previousDetail = queryClient.getQueryData<AdminUserDetailResponse>(
        adminKeys.userDetail(userId),
      );

      queryClient.setQueriesData<AdminUserListResponse>(
        { queryKey: adminKeys.usersRoot },
        (old) => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            items: old.items.map((user) =>
              user.id === userId ? applyPatchToUser(user, patch) : user,
            ),
          };
        },
      );

      if (previousDetail) {
        queryClient.setQueryData<AdminUserDetailResponse>(adminKeys.userDetail(userId), {
          ...previousDetail,
          ...applyPatchToUser(previousDetail, patch),
        });
      }

      return { previousListQueries, previousDetail, userId };
    },
    onError: (error, _variables, context) => {
      if (context?.previousListQueries) {
        for (const [queryKey, data] of context.previousListQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDetail !== undefined && context.userId) {
        queryClient.setQueryData(adminKeys.userDetail(context.userId), context.previousDetail);
      }

      const message = error instanceof Error ? error.message : "Failed to update user.";
      notifications.show({
        title: "Update failed",
        message,
        color: "red",
      });
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.usersRoot });
      if (variables?.userId) {
        queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(variables.userId) });
      }
    },
  });
}

/** Issue an API key on behalf of a user; plaintext is returned once. */
export function useIssueUserKey(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name }: IssueKeyVariables): Promise<AdminApiKeyCreatedResponse> => {
      const body: AdminCreateApiKeyRequest = { name };
      const { data, error, response } = await adminApiClient.POST(
        "/api/v1/admin/users/{user_id}/keys",
        {
          params: { path: { user_id: userId } },
          body,
        },
      );

      if (!response.ok || error || !data) {
        throw new Error(getApiErrorMessage(error, "Failed to issue API key."));
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Failed to issue API key.";
      notifications.show({
        title: "Issue key failed",
        message,
        color: "red",
      });
    },
  });
}

/** Revoke an API key by id. */
export function useRevokeKey(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ keyId }: RevokeKeyVariables): Promise<void> => {
      const { error, response } = await adminApiClient.DELETE("/api/v1/admin/keys/{key_id}", {
        params: { path: { key_id: keyId } },
      });

      if (!response.ok) {
        throw new Error(getApiErrorMessage(error, "Failed to revoke API key."));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      notifications.show({
        title: "Key revoked",
        message: "The API key will be rejected on the next request.",
        color: "green",
      });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Failed to revoke API key.";
      notifications.show({
        title: "Revoke failed",
        message,
        color: "red",
      });
    },
  });
}

/** Clear account login lockout for a user. */
export function useClearLoginLock(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<AdminUserUnlockResponse> => {
      const { data, error, response } = await adminApiClient.POST(
        "/api/v1/admin/users/{user_id}/unlock",
        {
          params: { path: { user_id: userId } },
        },
      );

      if (!response.ok || error || !data) {
        throw new Error(getApiErrorMessage(error, "Failed to unlock login."));
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      notifications.show({
        title: data.cleared ? "Login unlocked" : "No active lockout",
        message: data.cleared ? "The user can sign in again." : "This account was not locked.",
        color: data.cleared ? "green" : "blue",
      });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Failed to unlock login.";
      notifications.show({
        title: "Unlock failed",
        message,
        color: "red",
      });
    },
  });
}

function applyPatchToUser(user: AdminUserSummary, patch: AdminUserPatchRequest): AdminUserSummary {
  return {
    ...user,
    tier: patch.tier ?? user.tier,
    role: patch.role ?? user.role,
    is_active: patch.is_active ?? user.is_active,
  };
}
